import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { devWarn } from "../../lib/dev-log";
import CampaignCard from "./CampaignCard";
import CampaignShareDialog from "./CampaignShareDialog";
import LinkedInAssetsDialog from "../LinkedInAssetsDialog";
import { Button } from "../ui/button";
import {
  Plus, Sparkles, Rocket, Zap, Users, Eye,
} from "lucide-react";

const STATUS_FILTERS = [
  { key: "all",    label: "All" },
  { key: "active", label: "Active" },
  { key: "paused", label: "Paused" },
  { key: "draft",  label: "Draft" },
  { key: "archived", label: "Archived" },
];

/**
 * GrowthCampaignCentre — the module that replaces the old "Share" tab.
 *
 * Design intent:
 *  1. First hit: momentum tile-strip so the consultant sees Views / Assessments
 *     started / Leads captured across ALL campaigns at a glance.
 *  2. Below the strip: filterable grid of campaign cards. Card layout keeps
 *     Share as the primary CTA on every card (per amendment).
 *  3. Big "+ New campaign" always visible in the header — never buried.
 *  4. AI Insight banner appears when the consultant has ≥3 active campaigns
 *     and any of them has ≥5 views — nudges them to generate LinkedIn assets
 *     for the top performer.
 */
export default function GrowthCampaignCentre() {
  const nav = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [shareTarget, setShareTarget] = useState(null);
  const [linkedinTarget, setLinkedinTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/consultants/me/share-links");
      setCampaigns(data || []);
    } catch (err) {
      devWarn("[GrowthCampaignCentre] load campaigns failed", err);
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Roll-up metrics. Cheap client-side fold; stats per card lazy-load.
  const [roll, setRoll] = useState({ views: 0, completions: 0, leads: 0 });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!campaigns.length) { setRoll({ views: 0, completions: 0, leads: 0 }); return; }
      const results = await Promise.all(
        campaigns.map((c) =>
          api.get(`/consultants/me/share-links/${c.id}/stats`).then((r) => r.data).catch(() => null)
        )
      );
      if (cancelled) return;
      const agg = results.filter(Boolean).reduce(
        (acc, s) => ({
          views:       acc.views       + (s.total_opens       || 0),
          completions: acc.completions + (s.total_completions || 0),
          leads:       acc.leads       + (s.total_leads       || 0),
        }),
        { views: 0, completions: 0, leads: 0 }
      );
      setRoll(agg);
    })();
    return () => { cancelled = true; };
  }, [campaigns]);

  const filtered = useMemo(() => {
    if (filter === "all") return campaigns;
    return campaigns.filter((c) => (c.status || "active") === filter);
  }, [campaigns, filter]);

  const statusCounts = useMemo(() => {
    const c = { active: 0, paused: 0, draft: 0, archived: 0, completed: 0 };
    for (const x of campaigns) c[x.status || "active"] = (c[x.status || "active"] || 0) + 1;
    return c;
  }, [campaigns]);

  const topByViews = useMemo(() => {
    // Show AI-insight nudge if we have ≥3 campaigns and roll views ≥5.
    if (campaigns.length < 3 || roll.views < 5) return null;
    return campaigns[0]; // any active — the LinkedIn asset generator scopes per campaign
  }, [campaigns, roll]);

  const conversionPct = roll.views ? Math.round((roll.leads / roll.views) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="growth-campaign-centre">
      {/* Header: title + primary CTA (always the same primary intent — momentum) */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-xs uppercase tracking-widest text-muted-foreground">Growth Campaign Centre</div>
          <h2 className="mt-1 font-display text-2xl sm:text-3xl leading-tight text-foreground">
            Launch, share, and optimise your campaigns
          </h2>
          <p className="mt-1 text-sm text-muted-foreground max-w-xl">
            Every campaign here is a live audience segment. Share more, capture more.
          </p>
        </div>
        <Button size="lg" onClick={() => nav("/onboarding/first-campaign")} data-testid="centre-new-campaign">
          <Plus className="w-4 h-4 mr-2" /> New campaign
        </Button>
      </div>

      {/* Momentum strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="centre-momentum-strip">
        <Tile icon={Rocket}      label="Campaigns"    value={campaigns.length} sub={`${statusCounts.active} active`} />
        <Tile icon={Eye}         label="Views"        value={roll.views}       sub="Across all campaigns" />
        <Tile icon={Zap}         label="Discoveries"  value={roll.completions} sub="Completed by prospects" />
        <Tile icon={Users}       label="Leads"        value={roll.leads} tone="accent" sub={`${conversionPct}% view→lead`} />
      </div>

      {/* AI Insight — momentum nudge */}
      {topByViews && (
        <div className="rounded-xl border border-accent/30 bg-accent/5 p-4 sm:p-5 flex items-start gap-4" data-testid="centre-ai-insight">
          <div className="w-10 h-10 shrink-0 rounded-full bg-accent/15 text-accent flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-sans-ui font-semibold">Your campaigns are gaining traction</div>
            <p className="mt-1 text-sm text-muted-foreground">
              You have {roll.views} views across {campaigns.length} campaigns.
              Turn that momentum into inbound — generate a LinkedIn carousel + video script
              for &ldquo;{topByViews.name}&rdquo; in one click.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setLinkedinTarget(topByViews)} data-testid="centre-ai-insight-cta">
            Generate assets
          </Button>
        </div>
      )}

      {/* Status filter chips */}
      {campaigns.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="centre-filters">
          {STATUS_FILTERS.map((f) => {
            const active = filter === f.key;
            const count = f.key === "all" ? campaigns.length : (statusCounts[f.key] || 0);
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  active
                    ? "border-accent text-accent bg-accent/10"
                    : "border-border/60 text-muted-foreground hover:text-foreground hover:border-border"
                }`}
                data-testid={`centre-filter-${f.key}`}
              >
                {f.label} <span className="opacity-70">({count})</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="text-sm text-muted-foreground py-16 text-center">Loading campaigns…</div>
      ) : campaigns.length === 0 ? (
        <EmptyState onCreate={() => nav("/onboarding/first-campaign")} />
      ) : filtered.length === 0 ? (
        <div className="text-sm text-muted-foreground py-16 text-center border border-dashed border-border/60 rounded-xl">
          No campaigns in this filter.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="centre-grid">
          {filtered.map((c) => (
            <CampaignCard
              key={c.id}
              campaign={c}
              onShare={setShareTarget}
              onLinkedIn={setLinkedinTarget}
              onStatusChange={load}
              onDelete={load}
            />
          ))}
        </div>
      )}

      {/* Share dialog (primary momentum action) */}
      <CampaignShareDialog
        open={!!shareTarget}
        onOpenChange={(v) => !v && setShareTarget(null)}
        campaign={shareTarget}
      />

      {/* AI LinkedIn assets (reuses existing v1 component) */}
      <LinkedInAssetsDialog
        open={!!linkedinTarget}
        onOpenChange={(v) => !v && setLinkedinTarget(null)}
        link={linkedinTarget}
      />
    </div>
  );
}

function Tile({ icon: Icon, label, value, sub, tone }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card/60 p-4 flex flex-col gap-2">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground font-sans-ui">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`font-display text-3xl leading-none ${tone === "accent" ? "text-accent" : "text-foreground"}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="border border-dashed border-border/60 rounded-xl px-6 py-16 text-center">
      <div className="w-14 h-14 mx-auto rounded-full bg-accent/15 text-accent flex items-center justify-center">
        <Rocket className="w-6 h-6" />
      </div>
      <h3 className="mt-4 font-sans-ui font-semibold text-lg">Launch your first Growth Campaign™</h3>
      <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
        Every campaign becomes a live audience segment with its own landing page, QR, and share templates.
      </p>
      <Button size="lg" onClick={onCreate} className="mt-6" data-testid="centre-empty-cta">
        <Plus className="w-4 h-4 mr-2" /> New campaign
      </Button>
    </div>
  );
}
